/**
 * Convert a value with units to a common unit (millimeters)
 * Examples: "2.5mm" -> 2.5, "1cm" -> 10, "1m" -> 1000, "10μm" -> 0.01, "100nm" -> 0.0001
 * @param {string|number} value - The value with optional units
 * @returns {number} The normalized value in millimeters
 */
export const normalizeToMM = (value) => {
    if (typeof value === 'number') return value;
    if (typeof value !== 'string') return 0;
    
    // Extract the number
    const match = value.match(/^(\d+(?:\.\d+)?)/);
    if (!match) return 0;
    
    const num = parseFloat(match[1]);
    const unitMatch = value.match(/[a-zμ]+/i);
    const unit = unitMatch ? unitMatch[0].toLowerCase() : '';
    
    // Convert to millimeters
    switch (unit) {
        case 'nm': return num / 1000000;      // nanometers to mm
        case 'μm': return num / 1000;         // micrometers to mm
        case 'mm': return num;                 // already mm
        case 'cm': return num * 10;            // centimeters to mm
        case 'm': return num * 1000;           // meters to mm
        case 'kv': return num;                 // kV - no conversion (for beam energy)
        case 'v': return num;                  // V - no conversion
        default: return num;                   // assume same unit
    }
};

/**
 * Extract numeric value (without unit normalization)
 * Use this when units are the same (e.g., all values are in mm)
 */
export const extractNumericValue = (value) => {
    if (typeof value === 'number') return value;
    if (typeof value !== 'string') return 0;
    const match = value.match(/^(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
};

/**
 * Sort values by their actual physical size (normalized to mm)
 * This ensures: 1m (1000mm) > 2cm (20mm) > 5mm (5mm)
 * For WD sorting: 2.5mm, 5mm, 10mm, 2cm, 1m
 * @param {Array} arr - Array of values with units
 * @returns {Array} Sorted array
 */
export const sortByPhysicalSize = (arr) => {
    return [...arr].sort((a, b) => {
        const aNorm = normalizeToMM(a);
        const bNorm = normalizeToMM(b);
        return aNorm - bNorm;
    });
};

/**
 * Sort values by physical size descending
 */
export const sortByPhysicalSizeDesc = (arr) => {
    return [...arr].sort((a, b) => {
        const aNorm = normalizeToMM(a);
        const bNorm = normalizeToMM(b);
        return bNorm - aNorm;
    });
};

/**
 * Sort by numeric value (assumes same units)
 * Use this for beam energies (1kV, 5kV, 15kV) where all have same unit
 */
export const sortByNumericValue = (arr) => {
    return [...arr].sort((a, b) => {
        const aNum = extractNumericValue(a);
        const bNum = extractNumericValue(b);
        return aNum - bNum;
    });
};

export const sortByNumericValueDesc = (arr) => {
    return [...arr].sort((a, b) => {
        const aNum = extractNumericValue(a);
        const bNum = extractNumericValue(b);
        return bNum - aNum;
    });
};